import type { WordSummary } from '@/schemas/word';
// TipTap Mark for internal word links: [text](#<numericId>) markdown, serialized
// by lib/markdown-tiptap.ts — the target is Tahiti's `pms_word.id` primary key
// (verified against live devdb, BUILD-368), not a slug. Wraps real, selectable
// text (not an atom node) — the link's display text is the mark's underlying
// text run. Colour/dangling state is resolved from `options.wordList` at render
// time. editor-canvas.tsx keeps that array's *identity* stable and mutates its
// contents in place as resolution completes — sufficient on its own for click
// resolution (read live at click time) but NOT for the visual class: ProseMirror
// reuses a node's existing DOM when its attrs/content haven't changed, so a mark
// rendered before its target resolved won't get `renderHTML` re-invoked just
// because the options changed. editor-canvas.tsx patches already-rendered
// `[data-word-link]` spans' className directly for that (via
// {@link wordLinkClassName}, shared here so the two never drift).
import { Mark, mergeAttributes } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';

export interface WordLinkOptions {
  wordList: WordSummary[];
  onNavigate: (wordId: string) => void;
}

/** Resolve a word-link span's class from its target id — shared by `renderHTML` (initial render) and editor-canvas.tsx's DOM-patch recolor (see its docblock for why a mark can't just be re-rendered). */
export function wordLinkClassName(id: string | null, wordList: WordSummary[]): string {
  const word = id ? wordList.find((w) => w.numericId === id) : null;
  return word
    ? `editor-word-link${word.dominantNoyauKey ? ` editor-word-link--${word.dominantNoyauKey}` : ''}`
    : 'editor-word-link editor-word-link--dangling';
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    wordLink: {
      setWordLink: (attrs: { id: string }) => ReturnType;
    };
  }
}

export const WordLink = Mark.create<WordLinkOptions>({
  name: 'wordLink',

  addOptions() {
    return { wordList: [], onNavigate: () => {} };
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-word-id'),
        renderHTML: (attrs) => ({ 'data-word-id': attrs.id }),
      },
      // Preserves a malformed/legacy target (no `#id`, e.g. a bare word — the
      // rare non-conforming tail found in live devdb) verbatim, so it round-trips
      // instead of collapsing to `#` on the next save. Never used for resolution.
      raw: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-word-link-raw'),
        renderHTML: (attrs) => (attrs.raw ? { 'data-word-link-raw': attrs.raw } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-word-link]' }];
  },

  renderHTML({ mark, HTMLAttributes }) {
    const id = mark.attrs.id ? String(mark.attrs.id) : null;
    const cls = wordLinkClassName(id, this.options.wordList);
    return ['span', mergeAttributes(HTMLAttributes, { 'data-word-link': '', class: cls }), 0];
  },

  addCommands() {
    return {
      setWordLink:
        (attrs) =>
        ({ commands }) =>
          commands.setMark(this.name, attrs),
    };
  },

  // Click-through navigation, mirroring the pre-TipTap editor's mousedown
  // interception: works while editing (unlike a plain <a>, which would just
  // place the caret) and in read-only mode alike.
  addProseMirrorPlugins() {
    const options = this.options;
    return [
      new Plugin({
        props: {
          handleClick: (_view, _pos, event) => {
            const target = event.target as HTMLElement | null;
            const el = target?.closest<HTMLElement>('[data-word-link]');
            if (!el) return false;
            const id = el.dataset.wordId;
            const word = id && options.wordList.find((w) => w.numericId === id);
            if (word) {
              options.onNavigate(word.id);
              return true;
            }
            return false;
          },
        },
      }),
    ];
  },
});
